import { Text, Tooltip } from "@chakra-ui/react";
import { UserActivityLogRO } from "@intentsify/dto";
import { DateFormat, formatDate } from "@intentsify/utils";
import { ColumnDef } from "@tanstack/react-table";
import { IconButton } from "components";
import { EmptyCell } from "components/Table/components/EmptyCell";
import { FiEye } from "react-icons/fi";

export type UserActivityLogTableRow = UserActivityLogRO &
  Record<string, unknown>;

type UserAuditLogColumnsOptions = {
  onDetailsClick?: (activityLog: UserActivityLogTableRow) => void;
};

export const formatUserActivityParams = (
  activityParams: Record<string, unknown> | null,
) => {
  if (!activityParams || Object.keys(activityParams).length === 0) {
    return "";
  }

  return JSON.stringify(activityParams);
};

const ActivityParamsCell = ({
  activityParams,
}: {
  activityParams: Record<string, unknown> | null;
}) => {
  const formattedActivityParams = formatUserActivityParams(activityParams);

  if (!formattedActivityParams) {
    return <EmptyCell />;
  }

  return (
    <Tooltip label={formattedActivityParams} hasArrow>
      <Text
        maxW="360px"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {formattedActivityParams}
      </Text>
    </Tooltip>
  );
};

const baseUserAuditLogColumns: ColumnDef<UserActivityLogTableRow>[] = [
  {
    header: "Logged At",
    accessorKey: "loggedAt",
    cell: ({ row }) => {
      return row.original.loggedAt ? (
        formatDate({
          date: new Date(row.original.loggedAt),
          format: DateFormat.DateTime,
        })
      ) : (
        <EmptyCell />
      );
    },
  },
  {
    header: "User",
    accessorKey: "userDisplayName",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.userDisplayName ? (
        row.original.userDisplayName
      ) : (
        <EmptyCell />
      ),
  },
  {
    header: "Email",
    accessorKey: "userEmail",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.userEmail ? row.original.userEmail : <EmptyCell />,
  },
  {
    header: "Company",
    accessorKey: "companyName",
    enableSorting: false,
    cell: ({ row }) =>
      row.original.companyName ? row.original.companyName : <EmptyCell />,
  },
  {
    header: "Activity Type",
    accessorKey: "activityName",
  },
  {
    header: "Activity Params",
    accessorKey: "activityParams",
    enableSorting: false,
    cell: ({ row }) => (
      <ActivityParamsCell activityParams={row.original.activityParams} />
    ),
  },
];

export const getUserAuditLogColumns = ({
  onDetailsClick,
}: UserAuditLogColumnsOptions = {}): ColumnDef<UserActivityLogTableRow>[] => {
  if (!onDetailsClick) {
    return baseUserAuditLogColumns;
  }

  return [
    ...baseUserAuditLogColumns,
    {
      id: "details",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <IconButton
          tooltip="View details"
          icon={<FiEye />}
          onClick={(event) => {
            event.stopPropagation();
            onDetailsClick(row.original);
          }}
        />
      ),
    },
  ];
};

export const userAuditLogColumns = getUserAuditLogColumns();
